import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { contactInfo } from '../data/mock';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vr-techsolutions/artifacts/9vnskgnq_Gemini_Generated_Image_lf1inhlf1inhlf1i.png";

const Footer = ({ setActiveTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  const goToSection = (tabId) => {
    if (location.pathname !== '/') {
      navigate('/');
      return;
    }
    if (setActiveTab) setActiveTab(tabId);
  };

  const footerLinks = [
    { id: 'home', label: 'Inicio' },
    { id: 'outlet', label: 'Outlet' },
    { id: 'equipos', label: 'Equipos Nuevos' },
    { id: 'servicio', label: 'Servicio Técnico' },
    { id: 'contacto', label: 'Contacto' }
  ];

  return (
    <footer className="bg-black border-t border-violet-900/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <motion.div 
              className="flex flex-col items-start gap-2 mb-4 cursor-pointer"
              onClick={() => goToSection('home')}
              whileHover={{ scale: 1.02 }}
            >
              <img 
                src={LOGO_URL} 
                alt="SEBINOR Computación" 
                className="h-16 w-auto object-contain"
                style={{ 
                  filter: 'drop-shadow(0 0 10px rgba(139, 92, 246, 0.3))'
                }}
              />
              <span 
                className="text-amber-400 text-lg ml-1"
                style={{ fontFamily: "'Dancing Script', cursive" }}
              >
                25 años con vos
              </span>
            </motion.div>
            <p className="text-gray-500 text-sm leading-relaxed">
              Tu experto en tecnología desde el diagnóstico hasta la solución. Servicio técnico profesional, venta de equipos y atención personalizada.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Enlaces Rápidos</h4>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => goToSection(link.id)}
                    className="text-gray-400 hover:text-violet-400 text-sm transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contacto</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-gray-400 text-sm">
                <Phone className="w-4 h-4 text-violet-400" />
                {contactInfo.phone}
              </li>
              <li className="flex items-center gap-2 text-gray-400 text-sm">
                <Mail className="w-4 h-4 text-violet-400" />
                {contactInfo.email}
              </li>
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <MapPin className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                {contactInfo.address}
              </li>
            </ul>
          </div>

          {/* Social & Hours */}
          <div>
            <h4 className="text-white font-semibold mb-4">Horarios</h4>
            <ul className="space-y-1 text-gray-400 text-sm mb-6">
              <li>{contactInfo.hours.weekdays}</li>
              {contactInfo.hours.sunday && <li>{contactInfo.hours.sunday}</li>}
            </ul>
            
            <h4 className="text-white font-semibold mb-3">Síguenos</h4>
            <div className="flex items-center gap-3">
              <a 
                href={contactInfo.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-violet-900/30 flex items-center justify-center text-gray-400 hover:text-pink-400 hover:border-pink-500/50 transition-all"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a 
                href={`https://wa.me/${contactInfo.social.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-violet-900/30 flex items-center justify-center text-gray-400 hover:text-green-400 hover:border-green-500/50 transition-all"
                aria-label="WhatsApp"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-violet-900/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} SEBINOR Computación. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/terminos"
                data-testid="footer-terminos-link"
                className="text-gray-500 hover:text-violet-400 text-sm transition-colors"
              >
                Términos de servicio
              </Link>
              <Link
                to="/privacidad"
                data-testid="footer-privacidad-link"
                className="text-gray-500 hover:text-violet-400 text-sm transition-colors"
              >
                Política de privacidad
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
